import { useMedicationGroup } from '@/hooks/useMedicationGroup';
import { useTRPC } from '@/utils/trpc';
import type { MedicationListRecord } from '@liza/fhir-utils';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { MedicationDetail } from '@liza/ui/components/records';
import { Spinner } from '@liza/ui/components/ui/spinner';
import {
  getMedicationReferenceIdsWithoutDisplay,
  hydrateMedicationReferenceDisplays,
} from '@liza/ui/utils/medicationDisplay';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  id: string;
  resourceType: 'MedicationRequest' | 'MedicationStatement';
  medplumPatientId: string;
};

/**
 * The medication detail panel. When the selected record is one the list collapsed into a group (same
 * RxNorm code — a self-reported plan and its prescription, or repeated self-reports of the same
 * drug), every record in that group renders stacked, the displayed one first, so the clinician sees
 * what the single row actually stood for rather than just its representative.
 *
 * The group is re-derived from the chart via `useMedicationGroup`; the single-record fetch below is
 * what the panel falls back to when the group lookup finds nothing for this id.
 *
 * The two sources are independent, so the panel gates on whichever one actually produced the records
 * it is about to render rather than on the single-record fetch alone: with records in hand it draws
 * them, and only an empty result consults the fetch's own loading/error state for what to say
 * instead. A transient failure on one source therefore cannot blank out data the other already
 * returned.
 */
export const Medication = ({ id, resourceType, medplumPatientId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();

  const { group, isPending: groupPending } = useMedicationGroup(medplumPatientId, id);

  const medicationRequestQuery = useQuery(
    trpc.patient.medicationRequest.get.queryOptions({ id }, { enabled: resourceType === 'MedicationRequest' }),
  );
  const medicationStatementQuery = useQuery(
    trpc.patient.medicationStatement.get.queryOptions({ id }, { enabled: resourceType === 'MedicationStatement' }),
  );
  const selectedRecord =
    resourceType === 'MedicationRequest'
      ? medicationRequestQuery.data?.medicationRequest
      : medicationStatementQuery.data?.medicationStatement;

  // The group's records, displayed one first; the single fetched record when the group lookup
  // resolved without a match for this id (or has not resolved yet).
  const records: MedicationListRecord[] = useMemo(() => {
    if (group) return [group.record, ...group.superseded];
    return selectedRecord ? [selectedRecord] : [];
  }, [group, selectedRecord]);

  const medicationReferenceIds = useMemo(() => getMedicationReferenceIdsWithoutDisplay(records), [records]);
  const medicationsLookupQuery = useQuery(
    trpc.patient.medication.batchGet.queryOptions(
      { ids: medicationReferenceIds },
      { enabled: medicationReferenceIds.length > 0 },
    ),
  );
  const hydratedRecords = useMemo(
    () => hydrateMedicationReferenceDisplays(records, medicationsLookupQuery.data?.medications ?? []),
    [records, medicationsLookupQuery.data?.medications],
  );

  // Records in hand: render them, whichever source they came from. Nothing below this point can
  // replace them with a spinner or an error belonging to the other, still-settling source.
  if (hydratedRecords.length > 0) {
    return (
      <div className="flex flex-col">
        {hydratedRecords.map((record) => (
          <MedicationDetail key={`${record.resourceType}-${record.id}`} medication={record} />
        ))}
      </div>
    );
  }

  // No records yet. The group lookup still running is its own loading state — the single-record fetch
  // may already have settled empty, and reporting "not found" while the group is mid-flight would be
  // premature.
  if (groupPending) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner className="size-6" />
      </div>
    );
  }

  // Neither source produced anything: defer to the single-record fetch for the reason (loading, an
  // error worth showing, or genuinely nothing). `QueryStateHandler` is generic over its query's data
  // shape, so the two `get` queries can't collapse into one variable without widening the union past
  // what it accepts — hence the branch. `children` never runs: it is reached only when the query has
  // data, and data here means `hydratedRecords` was non-empty and returned above.
  if (resourceType === 'MedicationRequest') {
    return (
      <QueryStateHandler query={medicationRequestQuery} emptyMessage={t('noMedicationsFound')}>
        {() => null}
      </QueryStateHandler>
    );
  }

  return (
    <QueryStateHandler query={medicationStatementQuery} emptyMessage={t('noMedicationsFound')}>
      {() => null}
    </QueryStateHandler>
  );
};
